import { ApiMessage, ApiResponseHandler } from '../../ApiResponseHandler';
import ThomApiClient from '../../ThomApiClient';
import { StatusType } from '../../../pages/activitySheet/ActivityTableRow';
import { DateString, EmployeeType } from '../../../utils/type';
import dayjs from 'dayjs';
import { ActivitySheetEmployeeResponse } from '../../../pages/activitySheet/types';
import { TimeOffData } from '../../myTimeoff/MyTimeoffApi';
import { PayrollHistoryResponse } from '../../../pages/activitySheet/PayrollHistoryDialog';

export type ApptStatus = 'Pending' | 'Submitted';

export interface Appointment {
  appointmentId: string;
  appointmentType: string;
  groupBillingStartTime: string | null;
  groupBillingDurationDec: number | null;
  sessionDurationDec: number | null;
  commonAppointmentId: string;
  attendeeRole: 'CLINICIAN' | 'GROUP_ASSISTANT' | 'INTERPRETER';
  progressNotesId: string;
  progressNotesStatus: 'Pending' | 'Completed' | 'In Progress' | 'Canceled';
  appStartTime: string;
  appEndTime: string;
  clientId: string;
  attendeeId: string;
  clientFirstName: string;
  clientMiddleName: string;
  clientLastName: string;
  clientSuffix?: string;
  archived: boolean;
  archivalStatus: ApptStatus | null;
  startTime: string | null;
  serviceCode: string | null;
  settingCode: string | null;
  coTreatment: string | null;
  modifier: string | null;
  description: string | null;
  isLastIndex: boolean;
  clientLocationType: string | null;
  visitId: string | null;
  originalStatus: StatusType | null;
  status: StatusType | null;
  onHold: boolean | null;
}

export interface Activity {
  activitySheetId: string;
  rowId: number;
  archived: boolean;
  appointmentId: string | null;
  isHoliday: boolean | null;
  holidayTitle: string | null;
  day: string | null;
  billableHoursDec: number | null;
  approvedNonBillableHoursDec: number | null;
  otherAssocWorkHrsDec: number | null;
  otherNonBillableHrsDec: number | null;
  lunchTimeIncluded: boolean | null;
  noShowTravelDocDec: number | null;
  isNonBillableUpdated: boolean | null;
  vacationHoursDec: number | null;
  bereavementHoursDec: number | null;
  hourlyTimeOffHoursDec: number | null;
  hoursOutDec: number | null;
  etHoursDec: number | null;
  holidayHoursDec: number | null;
  ltiHoursDec: number | null;
  flexHoursDec: number | null;
  profHoursDec: number | null;
  otherHours: string | null;
  sickHoursDec: number | null;
  pfmlHoursDec: number | null;
  juryDutyHoursDec: number | null;
  totalHoursDec: number | null;
  status: StatusType | null;
  payrollStatus: StatusType | null;
  activityStatus: 'Open' | 'Closed' | 'Pending';
  // actionRequired: 'YES' | 'NO' | 'NONE';
  description: string | null;
  archivalStatus: ApptStatus | null;
  appointments: Appointment[] | null;
  groupBilling: GroupAppointment[];
  originalStatus: StatusType | null;
  expand: boolean;
  payrollHistoryFields: string[] | null;
}

export interface GroupAppointment {
  apptTime: string;
  isGroupBilling: boolean;
  appointmentId: string;
  hasInterpreter: boolean;
  attendeeId: string;
  appointmentType: string;
  groupBillingStartTime: string | null;
  groupBillingDurationDec: number | null;
  appointments: Appointment[];
}

export interface Productivity {
  weekStart: string;
  weekEnd: string;
  percent: string;
}

export interface ActivitySheet {
  employeeId: string;
  hrsLeftToAchieveGoal: string | null;
  scheduledProductivity: string | null;
  totalBillableHoursDec: number | null;
  totalScheduledBillableHoursDec: number | null;
  totalNonBillableHoursDec: number | null;
  productivityThreshold: number | null;
  totalApprovedNonBillableHoursDec: number | null;
  totalWorkingHoursDec: number | null;
  totalWorkUsage: string | null;
  totalPayHoursDec: number | null;
  newTPHCalculated: boolean;
  productivityGoal: string | null;
  totalFlexHoursDec: number | null;
  startDate: string | null;
  endDate: string | null;
  activities: Activity[] | null;
  employeeType: EmployeeType | null;
  employeeEndDate: DateString;
  productivity: Productivity[] | null;
  status: StatusType | null;
  totalProductivity: string | null;
  currentWeekProductivityPercent: string | null;
  totalHolidayHoursDec: number | null;
  totalHoursOutDec: number | null;
  totalEtHoursDec: number | null;
  totalPfmlHoursDec: number | null;
  totalBereaveHoursDec: number | null;
  totalHourlyTimeOffHoursDec: number | null;
  totalLtiHoursDec: number | null;
  totalProfessionalHoursDec: number | null;
  totalOtherHours: string | null;
  totalSickHoursDec: number | null;
  totalNoShowTravelDocHoursDec: number | null;
  totalVacationHoursDec: number | null;
  totalJuryDutyHoursDec: number | null;
  totalOtherAssocWorkHrsDec: number | null;
  totalOtherNonBillableHrsDec: number | null;
  flexHoursAllowedDec: number | null;
  lunchWaiverRequired: boolean | null;
  lunchWaiverStartDate: string | null;
}

interface ActivityAppointment {
  activitySheetId: string | null;
  status: string | null;
  progressNotesIds: string[];
  groupBilling: GroupAppointment[];
}

export interface ActivitySubmitRequest {
  startDate: string;
  endDate: string;
  employeeId: string;
  activities: ActivityAppointment[];
}

export interface UpdateAppointment {
  activitySheetId: string;
  description?: string;
  totalHoursDec?: number | null;
  lunchTimeIncluded?: boolean | null;
  noShowTravelDocDec?: number | null;
  approvedNonBillableHoursDec?: number | null;
  otherNonBillableHrsDec?: number | null;
  otherAssocWorkHrsDec?: number | null;
  timeOffList?: TimeOffData[];
  activityDate?: string;
  employeeType: string | null;
}

interface OnHoldRequestBody {
  visitId: string;
  onHold: boolean;
}

export const getSubmittedActivitySheet = async (
  date: string,
): Promise<ActivitySheet> => {
  const timezone = encodeURIComponent(dayjs(new Date(date)).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ActivitySheet>(
      `review/charts?date=${date}&timezone=${timezone}`,
    ),
  );
};

export const getAllEmployeesActivitySheet = async (
  date: string,
  search: string,
  page: number,
  sort: string,
  sortType: string,
  payrollStatus: string[],
  billingStatus: string[],
): Promise<ActivitySheetEmployeeResponse> => {
  const newSort = sort === 'lastName' ? 'last_name' : sort;
  const timezone = encodeURIComponent(dayjs(new Date(date)).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ActivitySheetEmployeeResponse>(
      `review?date=${date}&timezone=${timezone}&search=${search}&page=${page}&size=10&sort=${newSort}&sortType=${sortType}&billingStatus=${billingStatus}&payrollStatus=${payrollStatus}`,
    ),
  );
};

export const getWeeklyActivitySheet = async (
  date: string,
  employeeId: string,
  needProductivity: boolean,
): Promise<ActivitySheet> => {
  const timezone = encodeURIComponent(dayjs(new Date(date)).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ActivitySheet>(
      `activities?employeeId=${employeeId}&date=${date}&timezone=${timezone}&productivity=${needProductivity}`,
    ),
  );
};

export const getApprovalActivities = async (
  date: string,
  employeeId: string,
  needProductivity: boolean,
): Promise<ActivitySheet> => {
  const timezone = encodeURIComponent(dayjs(new Date(date)).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ActivitySheet>(
      `approval?employeeId=${employeeId}&date=${date}&timezone=${timezone}&productivity=${needProductivity}`,
    ),
  );
};

export const submitWeeklyActivitySheet = async (
  requestBody: ActivitySubmitRequest,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<ActivitySubmitRequest, ApiMessage>(
      `activities/submit?timezone=${timezone}`,
      requestBody,
    ),
  );
};

export const submitActivitySheetByReviewer = async (
  requestBody: ActivitySubmitRequest,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<ActivitySubmitRequest, ApiMessage>(
      `activities/submit-by-reviewer?timezone=${timezone}`,
      requestBody,
    ),
  );
};

export const submitApprovedActivitySheet = async (
  requestBody: ActivitySubmitRequest,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<ActivitySubmitRequest, ApiMessage>(
      `approval/submit?timezone=${timezone}`,
      requestBody,
    ),
  );
};

export const archiveAnAppointment = async (
  activitySheetId: string,
  progressNotesId: string,
  archive: boolean,
) => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().delete(
      `activity?activitySheetId=${activitySheetId}&progressNotesId=${progressNotesId}&archive=${archive}`,
    ),
  );
};

export const updateAppointment = async (
  date: string,
  body: UpdateAppointment,
  isReviewScreen: boolean,
  employeeId: string,
) => {
  const timezone = encodeURIComponent(dayjs(new Date(date)).format('Z'));
  if (isReviewScreen) {
    return ApiResponseHandler(
      await ThomApiClient.getInstance().put<UpdateAppointment, ApiMessage>(
        `payroll/activity?isReviewScreen=${isReviewScreen}&employeeId=${employeeId}&timezone=${timezone}`,
        body,
      ),
    );
  } else {
    return ApiResponseHandler(
      await ThomApiClient.getInstance().put<UpdateAppointment, ApiMessage>(
        `activity?isReviewScreen=${isReviewScreen}&employeeId=${employeeId}&timezone=${timezone}`,
        body,
      ),
    );
  }
};

interface VisitNoteBody {
  activitySheetId: string;
  visitId: string;
  description: string;
}
export const updateAppointmentVisitNote = async (body: VisitNoteBody) => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<VisitNoteBody, ApiMessage>(
      'activity/visit',
      body,
    ),
  );
};

export const submitReviewedPayrollActivities = async (
  date: string,
  employeeId: string,
) => {
  const timezone = encodeURIComponent(dayjs(date).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<unknown, ApiMessage>(
      `payroll/submit?date=${date}&employeeId=${employeeId}&timezone=${timezone}`,
      {},
    ),
  );
};

export const getPayrollHistory = async (
  employeeId: string,
  field: string,
  date: string,
) => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<PayrollHistoryResponse[]>(
      `payroll/history?employeeId=${employeeId}&field=${field}&date=${date}`,
    ),
  );
};

export const getReviewPayrollDetails = async (
  date: string,
  employeeId: string,
  needProductivity: boolean,
) => {
  const timezone = encodeURIComponent(dayjs(date).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ActivitySheet>(
      `activities/payroll?date=${date}&employeeId=${employeeId}&productivity=${needProductivity}&timezone=${timezone}`,
    ),
  );
};

export const getReviewBillingDetails = async (
  date: string,
  employeeId: string,
  needProductivity: boolean,
) => {
  const timezone = encodeURIComponent(dayjs(date).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ActivitySheet>(
      `activities/billing?date=${date}&employeeId=${employeeId}&productivity=${needProductivity}&timezone=${timezone}`,
    ),
  );
};

export const onHoldActivitySheetVisit = async (
  visitId: string,
  onHold: boolean,
) => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().patch<OnHoldRequestBody, ApiMessage>(
      `progress-notes/visits/on-hold`,
      {
        visitId: visitId,
        onHold: onHold,
      },
    ),
  );
};
